import values from 'lodash/values';
import { type Point, toPoint, type Line, type DiagramStatus } from './toPortInfo.tsx';
import { CONTAINER_WIDTH, getContainerHeight } from './getLozengePosition.tsx';
import type { Positionable } from './ListEdgeDetails.tsx';
import { START_NODE_RADIUS, STATUS_HEIGHT } from './ellipsis.tsx';
import {
	type HorizontalSnapLine,
	type HorizontalSnappable,
	type Bounds,
	type SnapLine,
	type VerticalSnapLine,
	type VerticalSnappable,
	type Snappable,
	HorizontalAlignment,
	VerticalAlignment,
} from './VerticalSnappable.types.tsx';

const SNAP_THRESHOLD = 3;

/** Returns true if two values are within the {@link SNAP_THRESHOLD} */
function isInSnapDistance(a: number, b: number) {
	return Math.abs(a - b) < SNAP_THRESHOLD;
}

/** Key used to merge snappables to snap lines. Uniqueness determined by alignment and axis value */
const toSnappableKey = (snappable: Snappable) =>
	`${snappable.alignment}:${snappable.type === 'horizontal' ? snappable.x : snappable.y} `;

const horizontalAlignmentCombinations = [
	[HorizontalAlignment.left, HorizontalAlignment.left],
	[HorizontalAlignment.left, HorizontalAlignment.right],
	[HorizontalAlignment.left, HorizontalAlignment.middle],
	[HorizontalAlignment.middle, HorizontalAlignment.left],
	[HorizontalAlignment.middle, HorizontalAlignment.right],
	[HorizontalAlignment.middle, HorizontalAlignment.middle],
	[HorizontalAlignment.right, HorizontalAlignment.left],
	[HorizontalAlignment.right, HorizontalAlignment.right],
	[HorizontalAlignment.right, HorizontalAlignment.middle],
] as const;

const verticalAlignmentCombinations = [
	[VerticalAlignment.top, VerticalAlignment.top],
	[VerticalAlignment.top, VerticalAlignment.bottom],
	[VerticalAlignment.bottom, VerticalAlignment.top],
	[VerticalAlignment.bottom, VerticalAlignment.bottom],
];

/**
 * Given a 'subject' rectangle, find all possible snappable lines.
 *
 * Snappable is a term here that is a _potential_ snap line, actual snap lines
 * require deduping and are done in {@link aggregateSnapLines} with the results
 * from this function.
 *
 * @param subject Item that is being dragged
 * @param others Items that can be aligned to
 * @returns List of possible snap lines, called {@link Snappable}s
 */
export const findAllSnappableLines = (subject: Bounds, others: Bounds[]): Snappable[] => {
	if (others.length === 0) return [];

	// find closest horizontal rays of same x value
	let closestDistanceX = Number.POSITIVE_INFINITY;
	let horizontalSnappables: HorizontalSnappable[] = [];

	horizontalAlignmentCombinations.forEach(([subjectAlignment, otherAlignment]) => {
		others.forEach((other) => {
			if (isInSnapDistance(subject[subjectAlignment], other[otherAlignment])) {
				const distance = Math.abs(subject[subjectAlignment] - other[otherAlignment]);

				if (distance < closestDistanceX) {
					// closer snappable exists on this axis, clear list
					closestDistanceX = distance;
					horizontalSnappables = [];
				}

				horizontalSnappables.push({
					type: 'horizontal',
					x: other[otherAlignment],
					alignment: subjectAlignment,
					top: other.top,
					bottom: other.bottom,
				});
			}
		});
	});

	// find closest verticals rays of same y value
	let closestDistanceY = Number.POSITIVE_INFINITY;
	let verticalSnappables: VerticalSnappable[] = [];

	verticalAlignmentCombinations.forEach(([subjectAlignment, otherAlignment]) => {
		others.forEach((other) => {
			if (isInSnapDistance(subject[subjectAlignment], other[otherAlignment])) {
				const distance = Math.abs(subject[subjectAlignment] - other[otherAlignment]);

				if (distance < closestDistanceY) {
					// closer snappable exists on this axis, clear list
					closestDistanceY = distance;
					verticalSnappables = [];
				}

				verticalSnappables.push({
					type: 'vertical',
					y: other[otherAlignment],
					alignment: subjectAlignment,
					left: other.left,
					right: other.right,
				});
			}
		});
	});

	return [...horizontalSnappables, ...verticalSnappables];
};

/**
 * Merges the possible snap lines (called Snappables) by
 * 1. keying the alignment and the x or y value
 * 2. getting the min/max for the other axis values (e.g. top and bottom
 *    for horizontal snappables) which includes the subject's values
 *
 * @param subject Item being dragged
 * @param snappables individual lines that can be snapped to
 * @returns Merged list of snap lines
 */
export const aggregateSnapLines = (subject: Bounds, snappables: Snappable[]): SnapLine[] => {
	if (snappables.length === 0) return [];

	const mapped = snappables.reduce(
		(acc, snappable) => {
			if (snappable.type === 'horizontal') {
				// oxlint-disable-next-line typescript/consistent-type-assertions
				const existingSnapLine = acc[toSnappableKey(snappable)] as HorizontalSnapLine;

				if (existingSnapLine) {
					// if exists, just update the top-most/bottom-most values
					existingSnapLine.top = Math.min(existingSnapLine.top, snappable.top, subject.top);
					existingSnapLine.bottom = Math.max(
						existingSnapLine.bottom,
						snappable.bottom,
						subject.bottom,
					);
				} else {
					const newSnapLine: SnapLine = {
						type: 'horizontal',
						x: snappable.x,
						alignment: snappable.alignment,
						top: Math.min(snappable.top, subject.top),
						bottom: Math.max(snappable.bottom, subject.bottom),
					};

					acc[toSnappableKey(snappable)] = newSnapLine;
				}
			} else if (snappable.type === 'vertical') {
				// oxlint-disable-next-line typescript/consistent-type-assertions
				const existingSnapLine = acc[toSnappableKey(snappable)] as VerticalSnapLine;

				if (existingSnapLine) {
					// if exists, just update the left-most/right-most values
					existingSnapLine.left = Math.min(existingSnapLine.left, snappable.left, subject.left);
					existingSnapLine.right = Math.max(existingSnapLine.right, snappable.right, subject.right);
				} else {
					const newSnapLine: SnapLine = {
						type: 'vertical',
						y: snappable.y,
						alignment: snappable.alignment,
						left: Math.min(snappable.left, subject.left),
						right: Math.max(snappable.right, subject.right),
					};

					acc[toSnappableKey(snappable)] = newSnapLine;
				}
			}

			return acc;
		},
		// oxlint-disable-next-line typescript/consistent-type-assertions
		{} as Record<string, SnapLine>,
	);

	return values(mapped);
};

/**
 * Returns the 'snapped' position of the subject given pre-calculated snap lines catering for snap alignemnt (eg right, middle, bottom etc).
 *
 * @param subject Item that is being dragged
 * @param snapLines Snap lines calculated from {@link aggregateSnapLines}
 * @returns
 */
export const snapSubject = (subject: Bounds, snapLines: SnapLine[]): Point => {
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const firstHorizontal = snapLines.find((snapLine) => snapLine.type === 'horizontal') as
		| HorizontalSnapLine
		| undefined;

	// oxlint-disable-next-line typescript/consistent-type-assertions
	const firstVertical = snapLines.find((snapLine) => snapLine.type === 'vertical') as
		| VerticalSnapLine
		| undefined;

	let x = subject.left;
	let y = subject.top;

	const subjectWidth = subject.right - subject.left;

	if (firstHorizontal?.alignment === 'left') {
		x = firstHorizontal.x;
	} else if (firstHorizontal?.alignment === 'middle') {
		x = firstHorizontal.x - subjectWidth / 2;
	} else if (firstHorizontal?.alignment === 'right') {
		x = firstHorizontal.x - subjectWidth;
	}

	const subjectHeight = subject.bottom - subject.top;

	if (firstVertical?.alignment === 'top') {
		y = firstVertical.y;
	} else if (firstVertical?.alignment === 'bottom') {
		y = firstVertical.y - subjectHeight;
	}

	return toPoint(x, y);
};

export const toLines = (snapLines: SnapLine[]): Line[] =>
	snapLines.map((snapLine) => {
		if (snapLine.type === 'horizontal') {
			return {
				start: toPoint(snapLine.x, snapLine.top),
				end: toPoint(snapLine.x, snapLine.bottom),
			};
		}
		return {
			start: toPoint(snapLine.left, snapLine.y),
			end: toPoint(snapLine.right, snapLine.y),
		};
	});

/**
 * Returns the bounds of all items on the diagram -
 * start node, statuses, global loops container
 * - excluding the bounds for the subject being currently moving.
 */
export const getOtherBounds = (
	statuses: DiagramStatus[],
	statusWidths: Record<string, number>,
	subject: Positionable,
	startNodePosition: Point | undefined,
	globalLoopsContainerPosition: Point | undefined,
	globalLoopedTransitionsCount: number,
): Bounds[] => {
	// Status bounds that exclude the currently moving status (if any)
	const statusBounds: Bounds[] = statuses
		.filter((status) => status.id !== subject.id && status.position)
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		.map((status) => getBounds(status.position!, statusWidths[status.id], STATUS_HEIGHT));

	const containerHeight = getContainerHeight(globalLoopedTransitionsCount);

	if (subject.type === 'status') {
		startNodePosition &&
			statusBounds.push(getBounds(startNodePosition, START_NODE_RADIUS * 2, START_NODE_RADIUS * 2));
		globalLoopsContainerPosition &&
			statusBounds.push(getBounds(globalLoopsContainerPosition, CONTAINER_WIDTH, containerHeight));
	} else if (subject.type === 'start-node') {
		globalLoopsContainerPosition &&
			statusBounds.push(getBounds(globalLoopsContainerPosition, CONTAINER_WIDTH, containerHeight));
	} else if (subject.type === 'global-loops-container') {
		startNodePosition &&
			statusBounds.push(getBounds(startNodePosition, START_NODE_RADIUS * 2, START_NODE_RADIUS * 2));
	}

	return statusBounds;
};

/**
 * Returns the bounds of the current subject being moved.
 */
export const getSubjectBounds = (
	subject: Positionable,
	statusWidths: Record<string, number>,
	globalLoopedTransitionsCount: number,
): {
	left: number;
	middle: number;
	right: number;
	top: number;
	bottom: number;
} => {
	if (subject.type === 'status') {
		return getBounds(subject.position, statusWidths[subject.id], STATUS_HEIGHT);
	}

	if (subject.type === 'global-loops-container') {
		const containerHeight = getContainerHeight(globalLoopedTransitionsCount);
		return getBounds(subject.position, CONTAINER_WIDTH, containerHeight);
	}

	return getBounds(subject.position, START_NODE_RADIUS * 2, START_NODE_RADIUS * 2);
};

/**
 * Returns the bounds of an item given its position, width and height.
 */
export const getBounds = (
	position: Point,
	width: number,
	height: number,
): {
	left: number;
	middle: number;
	right: number;
	top: number;
	bottom: number;
} => ({
	left: position.x,
	middle: position.x + width / 2,
	right: position.x + width,
	top: position.y,
	bottom: position.y + height,
});
